import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, Users, Building2, Shield, FileText, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { logout } = useAuth();

  const navItems = [
    { path: '/admin', icon: Activity, label: 'Overview', description: 'Dashboard & analytics' },
    { path: '/admin/users', icon: Users, label: 'Users', description: 'Manage user accounts' },
    { path: '/admin/listings', icon: Building2, label: 'Listings', description: 'Property listings' },
    { path: '/admin/verifications', icon: Shield, label: 'Verifications', description: 'ID & address verification' },
    { path: '/admin/applications', icon: FileText, label: 'Applications', description: 'Lease applications' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-gray-200">
          <Link to="/admin" className="flex items-center space-x-2">
            <Shield className="h-8 w-8 text-indigo-600" />
            <span className="text-xl font-bold text-gray-900">Admin Portal</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map(({ path, icon: Icon, label, description }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-start p-3 rounded-lg transition-colors ${
                location.pathname === path
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`h-5 w-5 mt-0.5 ${
                location.pathname === path ? 'text-indigo-600' : 'text-gray-400'
              }`} />
              <div className="ml-3">
                <p className="font-medium">{label}</p>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
            </Link>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/admin/settings"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <Settings className="h-5 w-5 mr-2" />
              <span>Settings</span>
            </Link>
            <button
              onClick={logout}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <LogOut className="h-5 w-5 mr-2" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pl-64">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-8">
          <h1 className="text-xl font-semibold text-gray-900">
            {navItems.find(item => item.path === location.pathname)?.label || 'Admin Portal'}
          </h1>
        </header>

        {/* Content */}
        <main className="p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;